import { useCallback, useMemo, useState } from 'react';

import { IColumn, PINNED, TColumnFilterState } from '@private/data-grid';
import { useUpdateEffect } from '@private/hooks';

import { IGridColumn, IGridColumnsSettings } from 'api/portalUser/types';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import { getPresets, RECENT_FILTER_NAME } from 'hooks/usePageNavigation/helpers';
import { IUrlFilterSettings } from 'hooks/usePageNavigation/types';
import { useSaveInURL } from 'hooks/useSaveInURL';
import { useGetPortalUserConfig, useSaveFiltersPreset } from 'queries/portalUser';
import { setIsGridColumnSettingExpanded } from 'store/userConfiguration';
import { debounce } from 'utils/common';
import { IAllPageFilters, INullable } from 'utils/types';

import { getFiltersToSave } from '../Filters/Presets/helpers';

interface IGetColumnSettingsProps<T>
  extends Pick<
    IUseSortGridColumnsProps<T>,
    'baseOffColumns' | 'baseOnColumns' | 'columns' | 'pageKey'
  > {
  gridColumnsSettings?: IGridColumnsSettings;
  isSystemPreset?: boolean;
}

interface IUseSortGridColumnsProps<T> {
  baseOffColumns?: string[];
  baseOnColumns?: string[];
  columns: IColumn<T>[];
  ignoreList?: string[];
  isRedirectedPreset?: boolean;
  pageFilters?: IAllPageFilters;
  pageKey?: string;
}

const filterColumns = <T>({
  baseOffColumns,
  baseOnColumns,
  columns,
  gridColumnsSettings,
  isSystemPreset,
}: IGetColumnSettingsProps<T>) => {
  const foundColumns: [keyof T, IGridColumn][] = [];
  const notFoundColumns: [keyof T, IGridColumn][] = [];

  for (const column of columns) {
    const { dataKey, pinned: initialColumnPinned } = column;
    const currentColumnSetting =
      gridColumnsSettings?.[dataKey as keyof IGridColumnsSettings];

    const currentColumnOrder = currentColumnSetting?.order;
    const currentColumnPinned = currentColumnSetting?.pinned;

    if (
      currentColumnSetting &&
      typeof currentColumnOrder === 'number' &&
      Number.isFinite(currentColumnOrder) &&
      !foundColumns[currentColumnOrder]
    ) {
      let pinnedValue = currentColumnPinned;
      if (
        (initialColumnPinned === PINNED.FIXED_LEFT ||
          initialColumnPinned === PINNED.FIXED_RIGHT) &&
        currentColumnPinned !== PINNED.FIXED_LEFT &&
        currentColumnPinned !== PINNED.FIXED_RIGHT
      ) {
        pinnedValue = initialColumnPinned;
      }
      foundColumns[currentColumnOrder] = [
        dataKey,
        { ...currentColumnSetting, pinned: pinnedValue },
      ];
    } else {
      notFoundColumns.push([
        dataKey,
        {
          order: 0,
          pinned: initialColumnPinned,
          stretched: false,
          ...(isSystemPreset
            ? { visible: false }
            : {
                visible: baseOnColumns
                  ? baseOnColumns.includes(dataKey as string)
                  : !baseOffColumns?.includes(dataKey as string),
              }),
        },
      ]);
    }
  }

  return { foundColumns, notFoundColumns };
};

const getColumnSettings = <T>({
  baseOffColumns,
  baseOnColumns,
  columns,
  gridColumnsSettings,
  isSystemPreset,
  pageKey,
}: IGetColumnSettingsProps<T>): IGridColumnsSettings | undefined => {
  if (!pageKey) {
    return;
  }

  const { foundColumns, notFoundColumns } = filterColumns({
    baseOffColumns,
    baseOnColumns,
    columns,
    gridColumnsSettings,
    isSystemPreset,
  });

  const resultedArr = [...foundColumns, ...notFoundColumns].reduce<
    [keyof T, IGridColumn][]
  >((prev, item) => {
    if (item) {
      const [key, value] = item;
      prev.push([key, { ...value, order: prev.length }]);
      return prev;
    }
    return prev;
  }, []);

  return Object.fromEntries(resultedArr);
};

export const useGridColumnsSettings = <T>(props: IUseSortGridColumnsProps<T>) => {
  const { savedData: urlFilters } = useSaveInURL<IUrlFilterSettings>('pageFilters');
  const { data: portalUser } = useGetPortalUserConfig();
  const { mutate: savePreset } = useSaveFiltersPreset();
  const isGridColumnSettingExpanded = useAppSelector(
    (store) => store.userConfiguration.isGridColumnSettingExpanded,
  );
  const dispatch = useAppDispatch();

  const debouncedSavePreset = useMemo(() => debounce(savePreset, 700), [savePreset]);

  const { ignoreList, isRedirectedPreset, pageFilters, pageKey } = props;

  const { activeFilterPreset } = useMemo(
    () => getPresets({ pageKey, portalUser }),
    [pageKey, portalUser],
  );

  const { defaultGridColumnsSettings, initialGridColumnsSettings } = useMemo(() => {
    const initialGridColumnsSettings =
      urlFilters?.columns ||
      getColumnSettings({
        ...props,
        gridColumnsSettings: activeFilterPreset?.columns,
        isSystemPreset: activeFilterPreset?.isSystem,
      }) ||
      null;

    const defaultGridColumnsSettings = getColumnSettings(props);

    return { defaultGridColumnsSettings, initialGridColumnsSettings };
  }, [urlFilters?.columns, props, activeFilterPreset?.columns]);

  const [gridColumnsSettings, setGridColumnsSettings] = useState<
    INullable<IGridColumnsSettings>
  >(initialGridColumnsSettings);

  useUpdateEffect(() => {
    // This is helpful when columns on first render might be empty due to empty data or loading
    setGridColumnsSettings(initialGridColumnsSettings);
  }, [props.columns.length]);

  useUpdateEffect(() => {
    if (isRedirectedPreset && urlFilters?.columns) {
      setGridColumnsSettings(urlFilters.columns);
    }

    if (!isRedirectedPreset) {
      if (initialGridColumnsSettings) {
        setGridColumnsSettings(initialGridColumnsSettings);
        return;
      }

      if (defaultGridColumnsSettings) {
        setGridColumnsSettings(defaultGridColumnsSettings);
      }
    }
  }, [activeFilterPreset, isRedirectedPreset]);

  const onColumnSettingsExpansionChange = useCallback(
    (isExpanded: boolean) => {
      dispatch(setIsGridColumnSettingExpanded({ isExpanded }));
    },
    [dispatch],
  );

  const onOrderChange = useCallback(
    (columns: TColumnFilterState<T>) => {
      setGridColumnsSettings(columns);

      if (pageKey) {
        debouncedSavePreset({
          columns,
          filters: getFiltersToSave({ ignoreList, search: pageFilters?.search }),
          isRecent: true,
          name: RECENT_FILTER_NAME,
          pageKey,
        });
      }
    },
    [pageKey, debouncedSavePreset, pageFilters?.search, ignoreList],
  );

  const onColumnVisibilityChange = useCallback(
    (columns: TColumnFilterState<T>) => {
      setGridColumnsSettings(columns);

      if (pageKey) {
        debouncedSavePreset({
          columns,
          filters: getFiltersToSave({ columns, ignoreList, search: pageFilters?.search }),
          isRecent: true,
          name: RECENT_FILTER_NAME,
          pageKey,
        });
      }
    },
    [pageKey, debouncedSavePreset, pageFilters?.search, ignoreList],
  );

  const onColumnStretchChange = useCallback(
    (columns: TColumnFilterState<T>) => {
      setGridColumnsSettings(columns);

      if (!pageKey) {
        return;
      }

      if (activeFilterPreset) {
        return debouncedSavePreset({
          columns,
          filters: getFiltersToSave({ columns, ignoreList, search: pageFilters?.search }),
          isRecent: activeFilterPreset.isRecent,
          name: activeFilterPreset.name,
          pageKey,
          presetId: activeFilterPreset.id,
        });
      }

      debouncedSavePreset({
        columns,
        filters: getFiltersToSave({ columns, ignoreList, search: pageFilters?.search }),
        isRecent: true,
        name: RECENT_FILTER_NAME,
        pageKey,
      });
    },
    [pageKey, activeFilterPreset, debouncedSavePreset, pageFilters?.search, ignoreList],
  );

  return {
    gridColumnsSettings,
    isGridColumnSettingExpanded,
    onColumnSettingsExpansionChange,
    onColumnStretchChange,
    onColumnVisibilityChange,
    onOrderChange,
  };
};
